import { useCallback, useEffect, useRef, useState } from 'react'

export type PollingOptions = { intervalSeconds?: number }
export type PollingContextValue = {
  addPoller: (intervalSeconds?: number) => (() => void) | undefined
}

/**
 * Registers a polling request with the given interval. Automatically unsubscribes on unmount.
 */
export function usePolling(ctx: PollingContextValue, options?: PollingOptions) {
  const { addPoller } = ctx
  const { intervalSeconds } = options ?? {}
  useEffect(() => {
    return addPoller(intervalSeconds)
  }, [addPoller, intervalSeconds])
}

/**
 * Tracks arbitrary polling requests and produces the tightest interval to respect all consumers.
 * Each `addPoller` call registers a request with its own interval and returns an unsubscribe handle.
 */
export function usePollers() {
  const pollers = useRef(new Map<string, number>())
  const [effectivePollingInterval, setEffectivePollingInterval] = useState(0)

  const updateEffectivePollingInterval = useCallback(() => {
    const values = Array.from(pollers.current.values())
    const minValue = Math.min(...values)

    if (isFinite(minValue)) {
      setEffectivePollingInterval(Math.max(0, minValue))
    } else {
      setEffectivePollingInterval(0)
    }
  }, [])

  const removePoller = useCallback(
    (id: string) => {
      pollers.current.delete(id)
      updateEffectivePollingInterval()
    },
    [updateEffectivePollingInterval]
  )

  const addPoller = useCallback(
    (intervalSeconds?: number) => {
      if (!intervalSeconds || intervalSeconds <= 0) return

      const id = crypto.randomUUID()
      pollers.current.set(id, intervalSeconds)
      updateEffectivePollingInterval()

      // Return the unsubscribe function
      return () => {
        removePoller(id)
      }
    },
    [updateEffectivePollingInterval, removePoller]
  )

  return { addPoller, effectivePollingInterval }
}
